/** In-process attention snapshots keep scheduler Dates intact and actions tied to the current plan. */
import { useEffect, useRef, useState } from 'react';
import type { CatchUpSuggestion } from '../../utils/calendarAutoScheduler';
import type { AtRiskTask } from './feedback';
import type { CalendarAutomaticPlanningControlProps } from './CalendarAutomaticPlanningControl';

export const TASK_QUEUE_DATA_EVENT = 'calendar:task-queue-data';
export const TASK_QUEUE_REQUEST_EVENT = 'calendar:task-queue-request';
export const TASK_QUEUE_ACTION_EVENT = 'calendar:task-queue-action';
export const TASK_QUEUE_READY_EVENT = 'calendar:task-queue-ready';

export interface CalendarTaskQueueData {
  automaticPlanning?: Omit<CalendarAutomaticPlanningControlProps, 'onEnabledChange'>;
  tasks: AtRiskTask[];
  suggestions: CatchUpSuggestion[];
  conflictCount: number;
  /** Fixed task blocks remain visible but are not eligible for automatic movement. */
  fixableConflictCount?: number;
  attentionCount: number;
  loading?: boolean;
  pendingTaskIds: string[];
  fixingConflicts: boolean;
}

export type CalendarTaskQueueAction = {
  action: 'open-task' | 'show-deadline' | 'apply' | 'dismiss' | 'auto-fix' | 'set-automatic-planning';
  enabled?: boolean;
  taskId?: string;
  suggestionKey?: string;
};

export const EMPTY_TASK_QUEUE: CalendarTaskQueueData = {
  tasks: [], suggestions: [], conflictCount: 0, attentionCount: 0,
  pendingTaskIds: [], fixingConflicts: false,
  loading: true,
};

export function catchUpSuggestionKey(s: CatchUpSuggestion): string {
  const placement = s.kind === 'push_deadline'
    ? s.proposedDeadline?.toISOString() ?? ''
    : `${s.pieces}:${s.pieceMinutes}:${s.placements.map(p => `${p.start.toISOString()}/${p.end.toISOString()}`).join(',')}`;
  const reason = s.kind === 'push_deadline' ? `${s.cause}:${s.availableMinutes}` : '';
  return `${s.taskId}:${s.kind}:${s.title}:${s.durationMinutes}:${s.deadlineRiskFlagCount}:${reason}:${placement}`;
}

export function canApplyCatchUp(s: CatchUpSuggestion): boolean {
  return s.kind === 'push_deadline'
    ? Boolean(s.proposedDeadline)
    : s.pieces > 0 && s.placements.length === s.pieces;
}

export function countCalendarAttention(overdueIds: string[], riskIds: string[], conflictIds: string[]): number {
  return new Set([...overdueIds, ...riskIds, ...conflictIds]).size;
}

export function dispatchTaskQueueAction(detail: CalendarTaskQueueAction): void {
  window.dispatchEvent(new CustomEvent(TASK_QUEUE_ACTION_EVENT, { detail }));
}

export function useCalendarTaskQueueSnapshot(): CalendarTaskQueueData {
  const [data, setData] = useState(EMPTY_TASK_QUEUE);
  useEffect(() => {
    const onData = (event: Event) => {
      setData((event as CustomEvent<CalendarTaskQueueData | null>).detail ?? EMPTY_TASK_QUEUE);
    };
    window.addEventListener(TASK_QUEUE_DATA_EVENT, onData);
    window.dispatchEvent(new CustomEvent(TASK_QUEUE_REQUEST_EVENT));
    return () => window.removeEventListener(TASK_QUEUE_DATA_EVENT, onData);
  }, []);
  return data;
}

interface TaskQueueCallbacks {
  openTask: (task: { taskId: string }) => void;
  showDeadline: (task: AtRiskTask) => void;
  apply: (suggestion: CatchUpSuggestion) => Promise<void>;
  dismiss: (suggestion: CatchUpSuggestion) => void;
  autoFix: () => Promise<void>;
  setAutomaticPlanning?: (enabled: boolean) => void;
}

export function useCalendarTaskQueuePublisher(
  snapshot: Omit<CalendarTaskQueueData, 'pendingTaskIds' | 'fixingConflicts'>,
  callbacks: TaskQueueCallbacks,
): void {
  const [pending, setPending] = useState({ pendingTaskIds: [] as string[], fixingConflicts: false });
  const inFlight = useRef(new Set<string>());
  const current = useRef({ snapshot, callbacks });
  current.current = { snapshot, callbacks };
  const published = useRef<CalendarTaskQueueData>({ ...snapshot, ...pending });
  published.current = { ...snapshot, ...pending };

  useEffect(() => {
    const publish = () => window.dispatchEvent(new CustomEvent(TASK_QUEUE_DATA_EVENT, { detail: published.current }));
    const onAction = (event: Event) => {
      const detail = (event as CustomEvent<CalendarTaskQueueAction>).detail;
      if (!detail) return;
      const { snapshot: data, callbacks: actions } = current.current;
      if (detail.action === 'set-automatic-planning' && typeof detail.enabled === 'boolean' && data.automaticPlanning?.available && !data.automaticPlanning.pending) {
        actions.setAutomaticPlanning?.(detail.enabled);
        return;
      }
      const task = data.tasks.find(t => t.id === detail.taskId);
      const suggestion = data.suggestions.find(s => catchUpSuggestionKey(s) === detail.suggestionKey);
      if (detail.action === 'open-task' && task) actions.openTask({ taskId: task.id });
      if (detail.action === 'show-deadline' && task) actions.showDeadline(task);
      if (detail.action === 'dismiss' && suggestion && !inFlight.current.has(suggestion.taskId)) actions.dismiss(suggestion);
      if (data.loading) return;
      const key = detail.action === 'auto-fix' ? 'auto-fix' : suggestion?.taskId;
      const run = detail.action === 'auto-fix' && (data.fixableConflictCount ?? data.conflictCount) > 0
        ? actions.autoFix
        : detail.action === 'apply' && suggestion && canApplyCatchUp(suggestion)
          ? () => actions.apply(suggestion)
          : null;
      if (!run || !key || inFlight.current.has(key)) return;
      inFlight.current.add(key);
      const publishPending = () => setPending({
        pendingTaskIds: [...inFlight.current].filter(id => id !== 'auto-fix'),
        fixingConflicts: inFlight.current.has('auto-fix'),
      });
      publishPending();
      void Promise.resolve().then(run).finally(() => {
        inFlight.current.delete(key);
        publishPending();
      });
    };
    window.addEventListener(TASK_QUEUE_REQUEST_EVENT, publish);
    window.addEventListener(TASK_QUEUE_ACTION_EVENT, onAction);
    return () => {
      window.removeEventListener(TASK_QUEUE_REQUEST_EVENT, publish);
      window.removeEventListener(TASK_QUEUE_ACTION_EVENT, onAction);
      window.dispatchEvent(new CustomEvent(TASK_QUEUE_DATA_EVENT, { detail: null }));
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(TASK_QUEUE_DATA_EVENT, { detail: published.current }));
  }, [snapshot, pending]);
}
